export default class Serial {
    onSuccess = () => {}; // Callback for successful connection
    onFail = (errorMsg) => {}; // Callback for failed connection with error message
    onReceive = (data) => {}; // Callback for receiving data

    constructor() {
        this.open = false;

        this.textDecoder = undefined;
        this.readableStreamClosed = undefined;
        this.reader = undefined;

        this.port = undefined;

        this.outputStream = undefined;
        this.inputStream = undefined;

        this.baudRate = 9600;
        this.buffer = '';
        this.checkedForUpdate = false;
        this.checkingFirmware = false;

        this.usbProductId = undefined;
        this.usbVendorId = undefined;

        this.info = undefined;
    }

    // Check if the Web Serial API is supported in the browser
    supported() {
        return 'serial' in navigator;
    }

    // Request and open a serial port
    async requestPort() {
        await this.close(); // Ensure any existing connection is closed

        const filters = [
            { usbVendorId: 0x1A86, usbProductId: 0x7523 }, // Common CH340 device IDs
            { usbVendorId: 0x1A86, usbProductId: 0x55D4 }  // Another possible CH340 device ID
        ];

        try {
            this.port = await navigator.serial.requestPort({ filters });

            this.usbProductId = this.port.getInfo().usbProductId;
            this.usbVendorId = this.port.getInfo().usbVendorId;

            //this.info = this.port.getInfo();

            //console.log(`[SERIAL] Port selected: ${this.usbVendorId}:${this.usbProductId}`);
            //console.log(`[SERIAL] Port info:`, this.info);
        } catch (e) {
            this.onFail('Port selection was cancelled or failed');
            return false; // Return false to indicate failure
        }

        return await this.openPort(); // Attempt to open the selected port
    }

    getInfo() {

        return this.port.getInfo();

    }

    // Open the serial port and set up event listeners
    async openPort() {
        if (!this.port) {
            const errorMessage = 'No port selected';
            this.onFail(errorMessage);
            return false; // Return false to indicate failure
        }

        try {
            await this.port.open({ baudRate: this.baudRate }); // Open the port with the specified baud rate
        } catch (e) {
            if (e.message.includes('Failed to open serial port')) {
                this.onFail('Port is already in use. Please close any other applications that might be using the port.');
            } else {
                this.onFail(`Failed to open port: ${e.message}`);
            }
            return false; // Return false to indicate failure
        }

        console.log(`[SERIAL] Connected`);

        this.port.addEventListener('disconnect', () => {
            const errorMessage = `[SERIAL] Disconnected!`;
            this.onFail(errorMessage);
        });

        this.outputStream = this.port.writable;
        this.inputStream = this.port.readable;

        this.onSuccess(); // Trigger success callback
        this.open = true;

        this.read(); // Start reading from the port

        return true; // Return true to indicate success
    }

    // Continuously read data from the input stream
    async read() {
        if (!this.port || !this.port.readable) {
            this.onFail('Port is not open or readable');
            return;
        }

        this.textDecoder = new window.TextDecoderStream();
        this.readableStreamClosed = this.port.readable.pipeTo(this.textDecoder.writable);
        this.reader = this.textDecoder.readable.getReader();

        try {
            while (true && this.open) {
                const { value, done } = await this.reader.read();
                if (done) {
                    break; // Exit loop if reader is done
                }
                if (value) {
                    this.buffer += value;
                    this.processBuffer(); // Process the buffer
                }
            }
        } catch (error) {
            this.onFail('Error reading data');
        } finally {
            await this.close(); // Ensure the port is closed on error
        }
    }

    // Process the buffer to extract complete messages
    processBuffer() {
        const carriageReturnIndex = this.buffer.indexOf('\r');
    
        if (carriageReturnIndex !== -1) {
            const message = this.buffer.substring(0, carriageReturnIndex).trim();
            this.buffer = this.buffer.substring(carriageReturnIndex + 1); // Remove processed message from the buffer
    
            console.log(`[SERIAL] Processing message: ${message}`); // Debugging log
    
            const versionPattern = /^[0-9]+v[0-9]+$/i; // Regex for firmware version patterns
    
            if (versionPattern.test(message)) { 
                if (this.checkingFirmware) {
                    this.handleFirmwareVersion(message); 
                } else {
                    this.onReceive(message); 
                }
            } else if (message === "$") {
                this.onReceive({ type: "uuid_exists" });
                console.log(`[SERIAL] UUID already exists on device.`);
            } else if (message === "!$") {
                this.onReceive({ type: "uuid_not_set" });
                console.log(`[SERIAL] UUID not set on device.`);
            } else if (/^u[A-F0-9]{6}$/i.test(message)) {
                this.onReceive({ type: "uuid", data: message });
                console.log(`[SERIAL] Received UUID from device: ${message}`);
            } else if (message.length >= 15) {
                const completeMessage = message.substring(0, 16);
                this.onReceive({ type: "message", data: completeMessage });
                console.log(`[SERIAL] Received complete message: ${completeMessage}`);
            } else {
                console.warn('[SERIAL] Received incomplete message:', message);
            }
        }
    }
    

    // Send a string value over the serial connection
// Send a string value over the serial connection
async send(value) {
    if (!this.open) {
        this.onFail('[SERIAL] Cannot send data: Port is not open');
        return;
    }

    if (!this.outputStream) {
        this.onFail('[SERIAL] Cannot send data: Port is not writable');
        return;
    }

    console.log(`[SERIAL] Sent: ${value}`);

    const encoder = new TextEncoder();
    const writer = this.outputStream.getWriter();

    try {
        await writer.write(encoder.encode(`${value}\r\n`)); // Write the encoded string with newline to the output stream
    } catch (error) {
        this.onFail('Failed to send data');
    } finally {
        writer.releaseLock(); // Release the writer's lock
    }
}

    // Close the serial port
    async close() {
        if (this.open) {
            this.open = false; // Mark the port as closed
    
            if (this.reader) {
                try {
                    await this.reader.cancel();
                } catch (error) {
                    const errorMessage = error?.message || 'Reader cancellation failed';
                    console.error(`[SERIAL] Disconnected - Failed to cancel reader: ${errorMessage}`);
                    this.onFail(`[SERIAL] Disconnected`);
                }
            }
    
            if (this.readableStreamClosed) {
                try {
                    await this.readableStreamClosed;
                } catch (error) {

                    console.error(`[SERIAL] Disconnected - Failed to close readable stream`);
                    this.onFail(`[SERIAL] Disconnected`);
                    // Suppress the error if it's undefined, as it may not be critical
                    if (error?.message) {
                        console.error(`[SERIAL] Disconnected - Failed to close readable stream: ${error.message}`);
                        this.onFail(`[SERIAL] Disconnected`);
                    }
                }
            }
    
            if (this.port) {
                try {
                    await this.port.close();
                } catch (error) {
                    const errorMessage = error?.message || 'Port closure failed';
                    console.error(`[SERIAL] Disconnected - Failed to close port: ${errorMessage}`);
                    this.onFail(`[SERIAL] Disconnected - Failed to close port: ${errorMessage}`);
                }
            }
    
            console.log('[SERIAL] Closed');
        }
    }
    




    // Request the firmware version from the device
    async getFirmwareVersion() {
        this.checkingFirmware = true;
        await this.send('?');
    }

    // Check if the firmware needs to be updated
    async checkForUpdate({ latestVersion }) {
        return new Promise((resolve) => {
            this.originalOnReceive = this.onReceive; // Backup original onReceive callback
            this.onReceive = (data) => {
                const version = data.trim();
                console.log(`Current firmware version: ${version}`);

                resolve(version !== latestVersion); // Resolve true if an update is needed, else false
                this.checkingFirmware = false;
                this.restoreOriginalOnReceive(); // Restore original onReceive callback
            };
            this.getFirmwareVersion(); // Request the firmware version
        });
    }

    // Restore the original onReceive callback
    restoreOriginalOnReceive() {
        this.onReceive = this.originalOnReceive;
    }

    // Handle the received firmware version
    handleFirmwareVersion(version) {
        console.log(`[SERIAL] Firmware version received: ${version}`);
        this.onReceive(version); // Call the original onReceive for consistency
        this.checkingFirmware = false; // Clear the flag
    }

    // Set the baud rate for the serial connection
    setBaudRate(newBaudRate) {
        this.baudRate = newBaudRate;
    }
}
